import { useState } from 'react';
import { X, Phone, EnvelopeSimple, HandHeart } from '@phosphor-icons/react';

/**
 * Concierge intake modal — collects everything the concierge operator needs
 * BEFORE Stripe: verified email (double-entry), phone, source CRM, rough DB
 * size, and a preferred call window. Concierge is a flat ADD-ON on top of a
 * paid tier (tierName/tierAmount/addonAmount props) — one combined Stripe
 * payment, no per-record charges. All inputs set explicit text colors.
 */
export default function ConciergeIntakeModal({ open, onCancel, onConfirm, busy, tierName = '', tierAmount = 0, addonAmount = 0 }) {
  const [email1, setEmail1] = useState('');
  const [email2, setEmail2] = useState('');
  const [phone, setPhone] = useState('');
  const [sourceCrm, setSourceCrm] = useState('');
  const [destCrm, setDestCrm] = useState('');
  const [dbSize, setDbSize] = useState('');
  const [callWindow, setCallWindow] = useState('');
  const [error, setError] = useState('');

  if (!open) return null;

  const total = (tierAmount || 0) + (addonAmount || 0);
  const fmt = (n) => `$${Number(n).toLocaleString()}`;

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email1.trim());
  const match = emailOk && email1.trim().toLowerCase() === email2.trim().toLowerCase();
  const phoneOk = phone.trim().length >= 7;
  const ready = match && phoneOk && callWindow;

  const submit = (e) => {
    e.preventDefault();
    if (!match) { setError('Emails must match.'); return; }
    if (!phoneOk) { setError('A phone number is required — this is a scheduled-call service.'); return; }
    if (!callWindow) { setError('Pick a call window so we know when to reach you.'); return; }
    setError('');
    onConfirm({
      email: email1.trim().toLowerCase(),
      phone: phone.trim(),
      sourceCrm: sourceCrm || 'not sure',
      destCrm: destCrm || 'not sure',
      dbSize: dbSize || 'not sure',
      callWindow,
    });
  };

  const inputCls = 'w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm text-slate-900 caret-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-[#F39C12] focus:border-transparent';
  const labelCls = 'block text-xs font-bold text-slate-700 mb-1';

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[1000] flex items-center justify-center p-4" data-testid="mc-concierge-modal">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[92vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="bg-[#0a0e27] text-white px-6 py-4 rounded-t-lg flex items-start gap-3">
          <HandHeart size={22} weight="fill" className="text-[#F39C12] mt-0.5 shrink-0" />
          <div className="flex-1">
            <h3 className="font-black text-base leading-tight" data-testid="mc-concierge-title">Add Concierge — {tierName}</h3>
            <p className="text-xs text-white/60 mt-0.5">
              {fmt(total)} today — {tierName} ({fmt(tierAmount)}) + Concierge add-on ({fmt(addonAmount)}).
              A specialist runs your entire migration with you on a scheduled call. Flat price, no per-record charges.{' '}
              <a href="/migrate/concierge" target="_blank" rel="noreferrer" className="underline text-amber-300 hover:text-amber-200" data-testid="mc-concierge-how-link">
                See exactly how the call works →
              </a>
            </p>
          </div>
          <button onClick={onCancel} className="text-white/50 hover:text-white shrink-0" data-testid="mc-concierge-close" aria-label="Close">
            <X size={18} weight="bold" />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4 text-slate-900">
          <div>
            <label className={labelCls}>Email <span className="text-slate-400 font-normal">(receipt + confirmation go here)</span></label>
            <div className="relative">
              <EnvelopeSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="email" value={email1} onChange={(e) => setEmail1(e.target.value)}
                placeholder="you@yourcompany.com" className={`${inputCls} pl-9`} data-testid="mc-concierge-email-1" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Confirm email</label>
            <div className="relative">
              <EnvelopeSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="email" value={email2} onChange={(e) => setEmail2(e.target.value)}
                placeholder="Retype your email" className={`${inputCls} pl-9`} data-testid="mc-concierge-email-2" />
            </div>
            {email2 && (
              <p className={`text-xs mt-1 font-medium ${match ? 'text-emerald-600' : 'text-red-500'}`} data-testid="mc-concierge-email-match">
                {match ? '✓ Match.' : 'Emails don\u2019t match yet.'}
              </p>
            )}
          </div>
          <div>
            <label className={labelCls}>Phone <span className="text-slate-400 font-normal">(we call you at your chosen window)</span></label>
            <div className="relative">
              <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 (555) 123-4567" className={`${inputCls} pl-9`} data-testid="mc-concierge-phone" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Coming from</label>
              <select value={sourceCrm} onChange={(e) => setSourceCrm(e.target.value)}
                className={inputCls} data-testid="mc-concierge-crm">
                <option value="">Pick your CRM…</option>
                <option value="act">ACT!</option>
                <option value="goldmine">GoldMine</option>
                <option value="salesforce">Salesforce</option>
                <option value="hubspot">HubSpot</option>
                <option value="zoho">Zoho</option>
                <option value="pipedrive">Pipedrive</option>
                <option value="monday">Monday</option>
                <option value="other">Other / spreadsheets</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Moving to</label>
              <select value={destCrm} onChange={(e) => setDestCrm(e.target.value)}
                className={inputCls} data-testid="mc-concierge-dest">
                <option value="">Pick your new CRM…</option>
                <option value="salescrunch">SalesCrunchCRM</option>
                <option value="salesforce">Salesforce</option>
                <option value="hubspot">HubSpot</option>
                <option value="pipedrive">Pipedrive</option>
                <option value="zoho">Zoho CRM</option>
                <option value="monday">monday.com</option>
                <option value="insightly">Insightly</option>
                <option value="copper">Copper</option>
                <option value="capsule">Capsule</option>
                <option value="freshsales">Freshsales</option>
                <option value="keap">Keap</option>
                <option value="less-annoying">Less Annoying CRM</option>
                <option value="not-sure">Not sure yet</option>
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Database size (rough)</label>
            <select value={dbSize} onChange={(e) => setDbSize(e.target.value)}
              className={inputCls} data-testid="mc-concierge-size">
              <option value="">Best guess…</option>
              <option value="under_1k">Under 1,000</option>
              <option value="1k_7500">1,000 – 7,500</option>
              <option value="7500_20k">7,500 – 20,000</option>
              <option value="over_20k">20,000+</option>
              <option value="not_sure">No idea — that's fine</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Best time to call</label>
            <select value={callWindow} onChange={(e) => setCallWindow(e.target.value)}
              className={inputCls} data-testid="mc-concierge-window">
              <option value="">Pick a window…</option>
              <option value="Weekday mornings (9am–12pm ET)">Weekday mornings (9am–12pm ET)</option>
              <option value="Weekday afternoons (12pm–5pm ET)">Weekday afternoons (12pm–5pm ET)</option>
              <option value="Weekday evenings (5pm–8pm ET)">Weekday evenings (5pm–8pm ET)</option>
              <option value="ASAP — call me today if possible">ASAP — call me today if possible</option>
            </select>
            <p className="text-[11px] text-slate-400 mt-1">We confirm the exact time by email within a few business hours.</p>
          </div>

          {error && <p className="text-xs text-red-500 font-medium" data-testid="mc-concierge-error">{error}</p>}

          <div className="flex items-center gap-3 pt-1">
            <button type="button" onClick={onCancel}
              className="px-4 py-2.5 text-sm font-bold text-slate-500 hover:text-slate-700"
              data-testid="mc-concierge-cancel">
              Cancel
            </button>
            <button type="submit" disabled={busy || !ready}
              className="flex-1 bg-[#F39C12] hover:bg-[#E08C00] disabled:opacity-50 text-black py-2.5 text-sm font-black rounded-md transition-colors"
              data-testid="mc-concierge-continue">
              {busy ? 'Opening Stripe…' : `Continue to payment — ${fmt(total)} →`}
            </button>
          </div>
          <p className="text-[11px] text-slate-400 text-center">Secure Stripe checkout · One-time payment · Your old CRM is never touched — we only read exports.</p>
        </form>
      </div>
    </div>
  );
}
